import { useRouter } from "next/router";
import { useLayoutEffect, useState } from "react";

import getUserProperties from "@/users/getUserProperties";
import getCurrentLevel from "@/utils/getCurrentLevel/index";
import { isArticle } from "@/view/utils/contentTypeChecks";
import useStudentQuizGet from "@/view/utils/useStudentQuizGet";

import type { LangstonContentResponse, User } from "@/types/models";
import type { Assignment } from "@/types/models/Assignment.interface";
import type {
  ILegacyStudentReadingLevel,
  IRecommendedReadingLevel,
} from "@/types/models/ReadingLevel.interface";

const useLevelLock = (
  content: LangstonContentResponse | null | undefined,
  matchedAssignments: Assignment[],
  user: User,
  studentReadingLevel?: ILegacyStudentReadingLevel | IRecommendedReadingLevel,
) => {
  const { query } = useRouter();
  const contentId = Array.isArray(query["contentId"])
    ? query["contentId"]?.[0]
    : query["contentId"] || "";

  const { isStudent } = getUserProperties(user);

  const [isLevelLocked, setIsLevelLocked] = useState(false);

  const contentData = content?.data;
  const contentIsArticle = isArticle(contentData);
  const articleLevels = contentIsArticle ? contentData?.article_levels : [];
  const matchedAssignment = matchedAssignments?.[0];
  const isAssignmentLevelLocked =
    matchedAssignment?.is_reading_level_locked ||
    matchedAssignment?.grade_level_lock;
  const shouldLevelLockContent =
    isStudent && isAssignmentLevelLocked && contentIsArticle;
  const lockedLevel = shouldLevelLockContent
    ? getCurrentLevel({
        articleLevels,
        contentId: contentId || "",
        matchedAssignment,
        user,
        studentReadingLevel,
      })
    : undefined;

  const { data: quiz } = useStudentQuizGet(lockedLevel?.id);
  const quizIsCompleted = quiz?.[0]?.answers?.length;

  // useLayoutEffect is used here to avoid level selector flickering
  useLayoutEffect(() => {
    if (quiz && !quizIsCompleted) {
      setIsLevelLocked(true);
    }
  }, [JSON.stringify(quiz), JSON.stringify(matchedAssignments)]);

  const unlockLevel = () => {
    setIsLevelLocked(false);
  };

  return { isLevelLocked, unlockLevel };
};

export default useLevelLock;
